import {
  isComponentOfType,
  useStableCallback,
} from "@prismicio/editor-support/React"
import {
  Children,
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { theme, type ThemeKeys } from "../../theme"
import { Box } from "../Box"
import * as styles from "./OverflowContainer.module.css"

type Gap = Extract<ThemeKeys<"space">, number>

interface OverflowContext {
  numberOfVisibleItems: number
}

/**
 * Private context not to be used outside this component
 */
const OverflowContext = createContext<OverflowContext>({
  numberOfVisibleItems: 0,
})

interface OverflowContainerProps {
  children: ReactNode[]
  /**
   * simulates a gap by adding a right margin to each item,
   * required so that gap is included within width calculations
   */
  gap?: Gap
}

export function OverflowContainer(props: OverflowContainerProps) {
  const { children, gap = 8 } = props

  const {
    numberOfVisibleItems,
    invisibleItemsContainerRef,
    adornmentsContainerRef,
    items,
    adornments,
  } = useOverflow(children, gap)

  const adornmentsVisible = numberOfVisibleItems < items.length

  const visibleItems = items.slice(0, numberOfVisibleItems)

  const overflowContainerContextValue = useMemo(
    () => ({
      numberOfVisibleItems,
    }),
    [numberOfVisibleItems],
  )

  return (
    <div
      style={{ "--OverflowContainer-gap": theme.space[gap] }}
      className={styles.overflowContainer}
    >
      <OverflowContext.Provider value={overflowContainerContextValue}>
        {/**
         * Invisible Content is needed in order for us to paint and access the DOM attributes of
         * all of the children(including the ones in overflow), without this we cannot access their width,
         * meaning the visible content won't know how many items it can fit
         */}
        <div
          ref={invisibleItemsContainerRef}
          aria-hidden
          className={styles.invisibleItemContainer}
        >
          {items}
        </div>
        <div
          ref={adornmentsContainerRef}
          aria-hidden
          className={styles.invisibleAdornmentContainer}
        >
          {adornments}
        </div>
        <Box overflow="hidden" width="100%">
          {visibleItems}
          {adornmentsVisible && adornments}
        </Box>
      </OverflowContext.Provider>
    </div>
  )
}

interface OverflowItemProps {
  children: ReactNode
}

export function OverflowItem(props: OverflowItemProps) {
  const { children } = props

  return <div className={styles.overflowItem}>{children}</div>
}

interface OverflowAdornmentProps {
  adornment: ({
    numberOfVisibleItems,
  }: {
    numberOfVisibleItems: number
  }) => ReactNode
}

export function OverflowEndAdornment(props: OverflowAdornmentProps) {
  const { adornment } = props
  const { numberOfVisibleItems } = useContext(OverflowContext)

  return <Box flexShrink={0}>{adornment({ numberOfVisibleItems })}</Box>
}

/**
 * calculates the number of elements that fit within a single line,
 * @returns refs to be attached to appropriate elements
 * @returns numberOfVisibleItems denoting the index to split the items that fit on the first row
 */
function useOverflow(children: ReactNode[], gap: Gap) {
  const childNodes = Children.toArray(children)

  const items = childNodes.filter((child) =>
    isComponentOfType(child, OverflowItem),
  )

  const adornments = childNodes.filter((child) =>
    isComponentOfType(child, OverflowEndAdornment),
  )

  /**
   * references the container of the invisible items
   * attached to a resize listener to trigger the item layout calculation
   */
  const invisibleItemsContainerRef = useRef<HTMLDivElement>(null)

  /**
   * references the endAdornmentContainers, ensures we can take them into account
   * for width calculations
   */
  const adornmentsContainerRef = useRef<HTMLDivElement>(null)

  /**
   * tracks the index at which the visible items should be split
   */
  const [numberOfVisibleItems, setNumberOfVisibleItems] = useState(items.length)

  /**
   * recomputes the amount of items that can fit within the width
   * of the invisible container.
   *
   * Iterates through each item within the invisible container,
   * removes the width of the item from the container width each time,
   * if the available width becomes < 0, then not all items fit.
   */
  const recompute = useStableCallback(() => {
    const invisibleItemsContainer = invisibleItemsContainerRef.current
    if (!invisibleItemsContainer)
      throw new Error("invisible items container ref not assigned")

    const adornmentsContainer = adornmentsContainerRef.current
    if (!adornmentsContainer)
      throw new Error("adornment container ref not assigned")

    let containerWidth = invisibleItemsContainer.clientWidth
    const itemSizes = Array.from(invisibleItemsContainer.children).map(
      (element) => element.clientWidth,
    )
    const adornmentsSize = adornmentsContainer.clientWidth

    let newNumberOfVisibleItems = 0

    for (const [index, itemSize] of itemSizes.entries()) {
      containerWidth -= itemSize
      newNumberOfVisibleItems = index + 1
      // all items can fit
      if (containerWidth >= 0 && index === itemSizes.length - 1) {
        break
      }
      // not all items can fit
      if (containerWidth <= 0) {
        if (adornmentsSize > 0) {
          containerWidth -= adornmentsSize
        } else if (index !== itemSizes.length - 1) {
          // When no adornment is present, account for the right padding of the next item (unless it's the last one)
          containerWidth += gap
        }
        // Iterate through all items, add their widths until they the maximum width of the container has been reached (accounting addornments)
        while (containerWidth < 0 && newNumberOfVisibleItems !== 0) {
          containerWidth += itemSizes[newNumberOfVisibleItems - 1] || 0
          newNumberOfVisibleItems--
        }
        break
      }
    }

    if (numberOfVisibleItems !== newNumberOfVisibleItems) {
      setNumberOfVisibleItems(newNumberOfVisibleItems)
    }
  })

  /**
   * adds a resizeObserver to the invisible items container ref
   */
  useLayoutEffect(() => {
    const invisibleItemsContainer = invisibleItemsContainerRef.current
    if (!invisibleItemsContainer)
      throw new Error("invisible items container ref not assigned")

    const resizeObserver = new ResizeObserver(recompute)

    resizeObserver.observe(invisibleItemsContainer)

    return () => {
      resizeObserver.unobserve(invisibleItemsContainer)
      resizeObserver.disconnect()
    }
  }, [recompute])

  // fires recompute callback whenever the number of children changes
  useLayoutEffect(recompute, [Children.count(children), recompute])

  return {
    numberOfVisibleItems,
    items,
    adornments,
    invisibleItemsContainerRef,
    adornmentsContainerRef,
  }
}
